/**
 * formLayout.ts — Layout / view-mode configuration for the dynamic form system.
 *
 * Stored at `form_schema.layout` (JSON — no DB migration required).
 * Consumed by: the builder Design → Layout panel, the WYSIWYG design canvas,
 * FormPreviewRenderer, DynamicFormBuilder, and the public /forms/[token] page.
 */

export type FormViewMode = 'list' | 'step';
export type FormTransition = 'slide-up' | 'slide-left' | 'fade' | 'none';
export type SubmitButtonAlign = 'left' | 'center' | 'right' | 'stretch';
export type ProgressStyle = 'bar' | 'pills' | 'none';

export const stepButtonContainerClass = (align: SubmitButtonAlign): string => {
  switch (align) {
    case 'left': return 'justify-start';
    case 'center': return 'justify-center';
    case 'right': return 'justify-end';
    case 'stretch': return 'flex-col';
    default: return 'justify-end';
  }
};

export const stepButtonWidthClass = (align: SubmitButtonAlign): string => {
  return align === 'stretch' ? 'w-full' : 'w-auto';
};

export const listSubmitContainerClass = (align: SubmitButtonAlign): string => {
  switch (align) {
    case 'left': return 'flex justify-start';
    case 'center': return 'flex justify-center';
    case 'right': return 'flex justify-end';
    case 'stretch': return 'flex flex-col';
    default: return 'flex justify-end';
  }
};

export const listSubmitButtonClass = (align: SubmitButtonAlign): string => {
  return align === 'stretch' ? 'w-full' : 'w-full sm:w-auto';
};

/** Remove dangerous patterns from user-supplied custom CSS. */
export function sanitizeCustomCSS(css: string): string {
  return css
    .replace(/<\/?style[^>]*>/gi, '')
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/@import\b/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/expression\s*\(/gi, '')
    .replace(/behavior\s*:/gi, '')
    .replace(/-moz-binding/gi, '');
}

/** Wrap sanitised custom CSS in an @scope rule so it only affects the form. */
export function scopeCustomCSS(css: string | undefined | null, scopeClass: string): string | null {
  if (!css || !css.trim()) return null;
  return `@scope (.${scopeClass}) { ${sanitizeCustomCSS(css)} }`;
}
/** Step granularity — per-field for survey-style forms, per-section for operational. */
export type StepGranularity = 'field' | 'section';

export interface FormLayoutConfig {
  /** 'list' renders all sections stacked; 'step' is one-question-at-a-time. */
  view_mode: FormViewMode;
  /** How a step is chunked when view_mode === 'step'. */
  step_granularity: StepGranularity;
  /** Transition animation between steps. */
  transition: FormTransition;
  /** Advance automatically after a single-selection answer (radio/yes-no/rating). */
  auto_advance_single: boolean;
  /** Show a Back button in step view. */
  show_back_button: boolean;
  /** Block advancing past a step while required fields are unanswered. */
  block_skip_required: boolean;
  /** Right-to-left layout direction. */
  rtl: boolean;
  /** Prefix questions with a number chip ("1."). */
  show_field_numbers: boolean;
  /** Whether the submit button is shown (false = auto-submit on last step). */
  show_submit_button: boolean;
  /** Submit/Next button alignment. */
  submit_button_align: SubmitButtonAlign;
  /** Progress indicator style. */
  progress_style: ProgressStyle;
}

export const DEFAULT_FORM_LAYOUT: FormLayoutConfig = {
  view_mode: 'list',
  step_granularity: 'section',
  transition: 'slide-up',
  auto_advance_single: true,
  show_back_button: true,
  block_skip_required: false,
  rtl: false,
  show_field_numbers: true,
  show_submit_button: true,
  submit_button_align: 'right',
  progress_style: 'bar',
};

/** Merge a partial form_schema.layout with safe defaults. */
export function resolveFormLayout(partial?: Partial<FormLayoutConfig> | null): FormLayoutConfig {
  if (!partial) return { ...DEFAULT_FORM_LAYOUT };
  return { ...DEFAULT_FORM_LAYOUT, ...partial };
}

/** Rules-engine visibility snapshot passed by consumers. */
export interface StepRuleEngine {
  hiddenFields?: Set<string>;
  shownFields?: Set<string>;
}

/** Minimal shape for a section so `buildFormSteps` can be generic over the
 * concrete section/field types used by each renderer (public page vs preview
 * vs DynamicFormBuilder all have their own local interface aliases). */
export interface FormFieldLike {
  key: string;
  type: string;
  sort_order: number;
  conditional?: { field: string; value: unknown };
  conditional_logic?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface FormSectionLike<Field extends FormFieldLike = FormFieldLike> {
  key: string;
  title: string;
  sort_order: number;
  fields: Field[];
  goto_section?: string | null;
  [key: string]: unknown;
}

export type FormStep<Section extends FormSectionLike = FormSectionLike> =
  | { type: 'section'; section: Section }
  | { type: 'field'; section: Section; field: Section['fields'][number] };

/** Build the ordered list of steps for the configured view mode and granularity. */
export function buildFormSteps<Section extends FormSectionLike>(
  sections: Section[],
  layout: FormLayoutConfig,
  formData: Record<string, unknown>,
  rules?: StepRuleEngine | null,
): FormStep<Section>[] {
  const sorted = [...sections].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  if (layout.view_mode !== 'step') return sorted.map((section) => ({ type: 'section', section } as FormStep<Section>));

  const steps: FormStep<Section>[] = [];
  for (const section of sorted) {
    if (layout.step_granularity === 'section') {
      steps.push({ type: 'section', section } as FormStep<Section>);
      continue;
    }

    const fields = [...(section.fields || [])]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .filter((field) => isFieldVisible(field, formData, rules));

    for (const field of fields) {
      steps.push({ type: 'field', section, field } as FormStep<Section>);
    }
  }
  return steps;
}

/** Return a section shaped for the current step (single field for field granularity). */
export function getStepSection<Section extends FormSectionLike>(step: FormStep<Section>): Section {
  if (step.type === 'field') {
    return { ...step.section, fields: [step.field as Section['fields'][number]] } as Section;
  }
  return step.section;
}

/** True when the field's own conditional logic + rules-engine visibility says it should show. */
export function isFieldVisible(
  field: FormFieldLike,
  formData: Record<string, unknown>,
  rules?: StepRuleEngine | null,
): boolean {
  if (!isFieldShownByRules(field.key, rules)) return false;
  return isFieldConditionalVisible(field, formData);
}

function isFieldShownByRules(key: string, rules?: StepRuleEngine | null): boolean {
  if (!rules) return true;
  const hidden = rules.hiddenFields?.has(key) ?? false;
  const shown = rules.shownFields?.has(key) ?? false;
  return !(hidden && !shown);
}

function isFieldConditionalVisible(field: FormFieldLike, formData: Record<string, unknown>): boolean {
  const legacy = (field as Record<string, unknown>).conditional as
    | { field: string; value: unknown }
    | undefined;
  if (legacy) {
    let currentValue = formData[legacy.field];
    if (legacy.field === 'enforce_geofence' && currentValue === undefined) {
      currentValue = formData['requires_geofence'];
    }
    if (typeof legacy.value === 'boolean') return currentValue === legacy.value;
    return currentValue == legacy.value;
  }

  const cl = (field as Record<string, unknown>).conditional_logic as Record<string, unknown> | undefined;
  if (cl && typeof cl === 'object') {
    const dependsOn = (cl.depends_on ?? cl.dependsOn ?? cl.field) as string | undefined;
    if (!dependsOn || typeof dependsOn !== 'string') return true;

    const currentValue = formData[dependsOn];
    const expectedValue = cl.show_when ?? cl.equals ?? cl.value;
    const operator = String(cl.operator || 'equals');

    switch (operator) {
      case 'is_empty':
        return currentValue === null || currentValue === undefined || currentValue === '';
      case 'is_not_empty':
        return currentValue !== null && currentValue !== undefined && currentValue !== '';
      case 'not_equals':
        return Array.isArray(currentValue)
          ? !currentValue.includes(expectedValue)
          : currentValue != expectedValue;
      case 'contains':
        return String(currentValue ?? '')
          .toLowerCase()
          .includes(String(expectedValue ?? '').toLowerCase());
      case 'equals':
      default:
        if (expectedValue === undefined) return Boolean(currentValue);
        if (Array.isArray(currentValue)) return currentValue.includes(expectedValue);
        return currentValue == expectedValue;
    }
  }

  return true;
}

/** Field types where a single answer should auto-advance when layout.auto_advance_single is on. */
export function isAutoAdvanceField(field: FormFieldLike): boolean {
  return ['single_select', 'radio', 'yes_no', 'boolean', 'rating', 'nps', 'dropdown'].includes(field.type);
}
